import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Loader2, FileSearch, Check, X } from "lucide-react";
import { API } from "@/context/AuthContext";
import { inr } from "@/pages/Dashboard";

export const B2B_STATUS = {
  books_only: { label: "In books, not on portal", cls: "bg-rose-100 text-rose-700" },
  amount_differs: { label: "Amount differs", cls: "bg-amber-100 text-amber-800" },
  portal_only: { label: "On portal, not in books", cls: "bg-sky-100 text-sky-700" },
  suggested: { label: "Suggested match", cls: "bg-orange-100 text-orange-800" },
  booked_earlier: { label: "Booked in earlier month", cls: "bg-violet-100 text-violet-700" },
  contra: { label: "Contra (nets to zero)", cls: "bg-slate-100 text-slate-700" },
  matched: { label: "Matched", cls: "bg-emerald-100 text-emerald-700" },
};
const ISSUES = ["books_only", "amount_differs", "portal_only", "suggested"];

const Decide = ({ r, i, onDecide }) => {
  if (r.status === "suggested") return (
    <div className="flex gap-1 mt-1">
      <button type="button" onClick={() => onDecide(r, "accepted")} data-testid={`b2b-accept-${i}`} className="rounded border border-emerald-300 bg-emerald-50 px-1.5 text-[10px] text-emerald-800 hover:bg-emerald-100 transition-colors"><Check className="inline h-3 w-3" /> Accept</button>
      <button type="button" onClick={() => onDecide(r, "rejected")} data-testid={`b2b-reject-${i}`} className="rounded border border-rose-300 bg-rose-50 px-1.5 text-[10px] text-rose-700 hover:bg-rose-100 transition-colors"><X className="inline h-3 w-3" /> Reject</button>
    </div>
  );
  if (r.decided_by) return <button type="button" onClick={() => onDecide(r, "clear")} data-testid={`b2b-undo-${i}`} className="mt-1 block text-[10px] text-slate-500 underline">Accepted by {r.decided_by} · undo</button>;
  return null;
};

const MatchTable = ({ rows, onDecide }) => (
  <div className="max-h-[420px] overflow-auto">
    <table className="w-full text-xs" data-testid="b2b-match-table">
      <thead className="sticky top-0 bg-white"><tr className="text-slate-500 uppercase text-[10px] border-b">
        {["Status", "Supplier", "Portal invoice", "Books invoice", "Posting", "Portal tax", "Books tax", "Difference", "Note"].map((h, i) => <th key={h} className={`${i > 4 && i < 8 ? "text-right" : "text-left"} py-1 px-1`}>{h}</th>)}
      </tr></thead>
      <tbody>
        {rows.slice(0, 1500).map((r, i) => (
          <tr key={`${r.gstin}-${r.portal_inv}-${r.books_inv}-${i}`} className="border-b border-slate-100" data-testid={`b2b-row-${r.status}-${i}`}>
            <td className="py-1 px-1"><span className={`rounded-full px-2 py-0.5 text-[10px] whitespace-nowrap ${B2B_STATUS[r.status].cls}`}>{B2B_STATUS[r.status].label}</span>
              {r.confidence && <div className="text-[10px] text-orange-700 mt-0.5" data-testid={`b2b-confidence-${i}`}>{r.confidence} confidence</div>}</td>
            <td className="px-1 max-w-[200px]"><div className="truncate" title={r.name}>{r.name}</div><div className="font-mono text-[10px] text-slate-400">{r.gstin}</div></td>
            <td className="px-1 font-mono">{r.portal_inv || "—"}<div className="text-[10px] text-slate-400">{r.portal_date}{r.portal_source === "CDNR" ? " · note" : ""}{r.rcm ? " · RCM" : ""}</div></td>
            <td className="px-1 font-mono">{r.books_inv || "—"}<div className="text-[10px] text-slate-400">{r.doc}{r.books_gstin && r.books_gstin !== r.gstin ? ` · ${r.books_gstin}` : ""}</div></td>
            <td className="px-1 whitespace-nowrap">{r.posting_date || "—"}</td>
            <td className="px-1 text-right tabular-nums">{inr(r.portal_tax)}</td>
            <td className="px-1 text-right tabular-nums">{inr(r.books_tax)}</td>
            <td className={`px-1 text-right tabular-nums font-semibold ${Math.abs(r.difference) < 1 ? "text-slate-400" : "text-rose-700"}`}>{inr(r.difference)}</td>
            <td className="px-1 text-[11px] text-slate-600 min-w-[220px]">{r.note}<Decide r={r} i={i} onDecide={onDecide} /></td>
          </tr>
        ))}
      </tbody>
    </table>
    {rows.length > 1500 && <p className="text-[11px] text-slate-500 p-2">Showing 1,500 of {rows.length.toLocaleString()} — download the Excel for all.</p>}
  </div>
);

const RejectedTable = ({ rows, onDecide }) => (
  <div className="max-h-[420px] overflow-auto">
    <table className="w-full text-xs" data-testid="b2b-rejected-table">
      <thead className="sticky top-0 bg-white"><tr className="text-slate-500 uppercase text-[10px] border-b">{["Portal GSTIN", "Portal invoice", "Books GSTIN", "Books invoice", "Rejected by", ""].map((h) => <th key={h} className="text-left py-1 px-1">{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.portal_gstin}-${r.portal_inv}-${r.books_inv}`} className="border-b border-slate-100" data-testid={`b2b-rejected-row-${i}`}>
            <td className="py-1 px-1 font-mono">{r.portal_gstin}</td><td className="px-1 font-mono">{r.portal_inv}</td><td className="px-1 font-mono">{r.books_gstin}</td><td className="px-1 font-mono">{r.books_inv}</td>
            <td className="px-1">{r.by} · {(r.at || "").slice(0, 10)}</td>
            <td className="px-1"><button type="button" onClick={() => onDecide({ gstin: r.portal_gstin, portal_inv: r.portal_inv, books_gstin: r.books_gstin, books_inv: r.books_inv }, "clear")} data-testid={`b2b-rejected-undo-${i}`} className="text-[10px] text-sky-700 underline">Undo</button></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SupplierTable = ({ rows }) => (
  <div className="max-h-[420px] overflow-auto">
    <table className="w-full text-xs" data-testid="b2b-supplier-table">
      <thead className="sticky top-0 bg-white"><tr className="text-slate-500 uppercase text-[10px] border-b">{["Supplier", "GSTIN", "Invoices", "Issues", "Portal tax", "Books tax", "Gap"].map((h, i) => <th key={h} className={`${i > 1 ? "text-right" : "text-left"} py-1 px-1`}>{h}</th>)}</tr></thead>
      <tbody>
        {rows.slice(0, 500).map((x) => (
          <tr key={x.gstin} className="border-b border-slate-100" data-testid={`b2b-supplier-${x.gstin}`}>
            <td className="py-1 px-1 max-w-[240px] truncate" title={x.name}>{x.name}</td><td className="px-1 font-mono">{x.gstin}</td>
            <td className="px-1 text-right">{x.count}</td><td className={`px-1 text-right ${x.issues ? "text-rose-700 font-semibold" : ""}`}>{x.issues}</td>
            <td className="px-1 text-right tabular-nums">{inr(x.portal_tax)}</td><td className="px-1 text-right tabular-nums">{inr(x.books_tax)}</td>
            <td className={`px-1 text-right tabular-nums font-semibold ${Math.abs(x.gap) < 1 ? "text-emerald-700" : "text-rose-700"}`}>{inr(x.gap)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const GSTR2BB2BMatch = ({ period, version, source = "gstr2b" }) => {
  const label = source === "gstr2a" ? "GSTR-2A" : "GSTR-2B";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [status, setStatus] = useState("issues");
  const [view, setView] = useState("invoices");
  useEffect(() => {
    if (!period) return;
    setBusy(true);
    axios.get(`${API}/gst-returns/${source}/${period}/b2b-match`).then((r) => setData(r.data)).catch((e) => { setData(null); toast.error(e.response?.data?.detail || `${label} B2B match failed`); }).finally(() => setBusy(false));
  }, [period, version, source, label, tick]);
  const rows = useMemo(() => (data?.rows || []).filter((r) => status === "all" || (status === "issues" ? ISSUES.includes(r.status) : r.status === status)), [data, status]);

  const decide = async (r, decision) => {
    try {
      await axios.post(`${API}/gst-returns/b2b-decision`, { portal_gstin: r.gstin, portal_inv: r.portal_inv, books_gstin: r.books_gstin, books_inv: r.books_inv, decision });
      toast.success(decision === "accepted" ? "Match accepted — remembered for later months" : decision === "rejected" ? "Suggestion rejected" : "Decision cleared");
      setTick((t) => t + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  const download = async () => {
    try {
      const r = await axios.get(`${API}/gst-returns/${source}/${period}/b2b-match/xlsx`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `${label.replace("-", "")}_B2B_match_${period}.xlsx`; a.click();
    } catch { toast.error("Download failed"); }
  };

  if (!data) return busy ? <p className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Matching {label} invoices…</p> : null;
  const s = data.summary;
  return (
    <Card className="border-slate-200 shadow-sm" data-testid={`b2b-match-card-${source}`}>
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base flex items-center gap-2"><FileSearch className="h-4 w-4" /> {label} B2B + CDNR vs books — {period}</CardTitle>
          <p className="text-[11px] text-slate-500 mt-1">Supplier GSTIN + invoice no. (spaces, "/", "-" and leading zeros ignored) against the GSTR-2 register by posting date, also in the 3 earlier months (from {data.earlier_from}). Left-overs are scored on same tax / taxable value (±₹1, or 0.1% up to ₹10), look-alike invoice no., close dates and same PAN — accept or reject each suggestion. Entries of one supplier that cancel out are shown as contra. Portal tax {inr(data.totals.portal_tax)} · books tax {inr(data.totals.books_tax)} · register synced {(data.register_built_at || "").slice(0, 16).replace("T", " ")}.</p>
        </div>
        <Button size="sm" variant="outline" onClick={download} data-testid={`b2b-download-btn-${source}`}><Download className="h-4 w-4 mr-1" />Excel</Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {[["issues", `Issues (${ISSUES.reduce((n, k) => n + s[k].count, 0)})`], ...Object.keys(B2B_STATUS).map((k) => [k, `${B2B_STATUS[k].label}: ${s[k].count} · ${inr(k === "books_only" ? s[k].books_tax : s[k].portal_tax)}`]), ["all", "All"]].map(([k, l]) => (
            <button type="button" key={k} onClick={() => { setStatus(k); setView("invoices"); }} data-testid={`b2b-filter-${k}`}
              className={`rounded-full border px-2.5 py-0.5 text-[11px] transition-colors ${status === k && view === "invoices" ? "border-sky-400 bg-sky-50 text-sky-800" : "border-slate-200 bg-white text-slate-700 hover:border-sky-300"}`}>{l}</button>
          ))}
          <button type="button" onClick={() => setView("suppliers")} data-testid="b2b-view-suppliers"
            className={`rounded-full border px-2.5 py-0.5 text-[11px] ${view === "suppliers" ? "border-sky-400 bg-sky-50 text-sky-800" : "border-slate-200 bg-white text-slate-700"}`}>By supplier ({data.by_supplier.length})</button>
          {data.rejected?.length > 0 && <button type="button" onClick={() => setView("rejected")} data-testid="b2b-view-rejected"
            className={`rounded-full border px-2.5 py-0.5 text-[11px] ${view === "rejected" ? "border-sky-400 bg-sky-50 text-sky-800" : "border-slate-200 bg-white text-slate-700"}`}>Rejected suggestions ({data.rejected.length})</button>}
        </div>
        {view === "suppliers" ? <SupplierTable rows={data.by_supplier} /> : view === "rejected" && data.rejected?.length ? <RejectedTable rows={data.rejected} onDecide={decide} /> : <MatchTable rows={rows} onDecide={decide} />}
      </CardContent>
    </Card>
  );
};
